import Fuse from "fuse.js";

const DEFAULT_FUSE_OPTIONS = {
  // Lower = stricter. Typical range: 0.2–0.4.
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: 2,
  shouldSort: true,
};

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function toQueryString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function collectByPath(obj, parts, index) {
  if (obj == null) return [];

  // If we're at the end of the path, return the current value(s).
  if (index >= parts.length) {
    return Array.isArray(obj) ? obj : [obj];
  }

  // If the current value is an array, apply the same path part to each item.
  if (Array.isArray(obj)) {
    const results = [];
    for (const item of obj) {
      results.push(...collectByPath(item, parts, index));
    }
    return results;
  }

  const next = obj?.[parts[index]];
  return collectByPath(next, parts, index + 1);
}

function getValuesByPath(obj, path) {
  if (!path) return [];
  if (typeof path === "function") {
    try {
      return [path(obj)];
    } catch (_e) {
      return [];
    }
  }

  const parts = String(path).split(".");
  return collectByPath(obj, parts, 0);
}

function normalizeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
}

function basicTextFilter(items, query, keys) {
  const q = normalizeString(query);
  if (!q) return items;

  const normalizedKeys = Array.isArray(keys) && keys.length ? keys : ["name"];

  return items.filter((item) => {
    for (const key of normalizedKeys) {
      const values = getValuesByPath(item, key);
      for (const v of values) {
        if (normalizeString(v).includes(q)) return true;
      }
    }
    return false;
  });
}

/**
 * Shared fuzzy search helper for lists across the application.
 *
 * - Returns the original list if `query` is empty.
 * - Uses Fuse.js when possible.
 * - Falls back to a simple `includes` search if Fuse throws (bad keys, etc.).
 *
 * @param {Array<any>} items
 * @param {string} query
 * @param {{ keys?: Array<string|{name: string, weight?: number}>; fuseOptions?: Object; limit?: number }} config
 * @returns {Array<any>}
 */
export function fuseFilter(items, query, config = {}) {
  const list = Array.isArray(items) ? items : [];
  if (isBlank(query)) return list;

  const keys = config?.keys;
  const fuseOptions = {
    ...DEFAULT_FUSE_OPTIONS,
    ...(config?.fuseOptions || {}),
    ...(keys && keys.length ? { keys } : {}),
  };

  const q = toQueryString(query);

  try {
    const fuse = new Fuse(list, fuseOptions);
    const results = fuse.search(q, config?.limit ? { limit: config.limit } : undefined);
    return results.map((r) => r.item);
  } catch (_e) {
    return basicTextFilter(list, q, keys);
  }
}

/**
 * Same as `fuseFilter` but returns Fuse result objects (with score, matches, etc.).
 *
 * @param {Array<any>} items
 * @param {string} query
 * @param {{ keys?: Array<string|{name: string, weight?: number}>; fuseOptions?: Object; limit?: number }} config
 * @returns {Array<any>}
 */
export function fuseSearch(items, query, config = {}) {
  const list = Array.isArray(items) ? items : [];
  if (isBlank(query)) return [];

  const keys = config?.keys;
  const fuseOptions = {
    ...DEFAULT_FUSE_OPTIONS,
    includeScore: true,
    ...(config?.fuseOptions || {}),
    ...(keys && keys.length ? { keys } : {}),
  };

  const q = toQueryString(query);
  const fuse = new Fuse(list, fuseOptions);
  return fuse.search(q, config?.limit ? { limit: config.limit } : undefined);
}
